import React from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { isDefined, parseNumber } from "utils/fp";

import { useScreenshotContext } from "components/screenshot/hooks";

import { ShapefileLegendBase } from "./ShapefileLegendBase";
import { useShapefileLegendItems } from "./useShapefileLegendItems";

type ShapefileLegendWrapperProps = {
  readonly isScreenshotOverlayVisible: boolean;
};

const DEFAULT_TITLE_SIZE = 15;
const DEFAULT_LABELS_SIZE = 14;

export const ShapefileLegendWrapper: React.FC<ShapefileLegendWrapperProps> = ({
  isScreenshotOverlayVisible
}) => {
  const { settings } = useScreenshotContext();

  const selectedShapefile = useSelector(
    (state: RootState) => state.project.selectedShapefile
  );

  const showLegend = Boolean(settings?.showShapefileLegendInScreenshot);
  const showDetails =
    Boolean(settings?.shapefileLegendShowDetails) && isDefined(selectedShapefile);
  const showBorder = Boolean(settings?.showShapefileLegendBorderInScreenshot ?? true);
  const backgroundOpacity = parseNumber(settings?.shapefileLegendOpacity, 1);
  const isWrappingText = isScreenshotOverlayVisible
    ? Boolean(settings?.isWrappingTextShapefile ?? true)
    : true;

  const mapScreenshotScale = parseNumber(settings?.mapScreenshotScale, 1);

  const titleSize = isScreenshotOverlayVisible
    ? parseNumber(settings?.shapefileTitleSize, DEFAULT_TITLE_SIZE) * mapScreenshotScale
    : DEFAULT_TITLE_SIZE;

  const labelsSize = isScreenshotOverlayVisible
    ? parseNumber(settings?.shapefileLabelsSize, DEFAULT_LABELS_SIZE) * mapScreenshotScale
    : DEFAULT_LABELS_SIZE;

  const parentDimensions = {
    width: parseNumber(settings?.width, 1152) * mapScreenshotScale,
    height: parseNumber(settings?.height, 681) * mapScreenshotScale
  };

  const legendData = useShapefileLegendItems(showDetails);

  if (!showLegend) return null;

  return (
    <ShapefileLegendBase
      legendData={legendData}
      showLegend={showLegend}
      showBorder={showBorder}
      titleSize={titleSize}
      labelsSize={labelsSize}
      backgroundOpacity={backgroundOpacity}
      isWrappingText={isWrappingText}
      parentDimensions={parentDimensions}
      mapScreenshotScale={mapScreenshotScale}
      isScreenshotOverlayVisible={isScreenshotOverlayVisible}
    />
  );
};
